import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import './SignIn.css';

const SignIn = () => {
  const { siteName } = useSite();
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const { user, signIn, signUp } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    window.scrollTo(0, 0);
    if (user) {
      navigate('/account');
    }
  }, [user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }

    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }

    if (isSignUp) {
      if (!formData.name) {
        setError('Please enter your name');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      if (isSignUp) {
        await signUp(formData.name, formData.email, formData.password);
      } else {
        await signIn(formData.email, formData.password);
      }
      navigate('/account');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsSignUp(!isSignUp);
    setError('');
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    });
  };

  return (
    <div className="signin-page">
      <div className="signin-container">
        <div className="signin-left">
          <div className="signin-left-content">
            <h1 className="signin-left-title">Welcome to {siteName}</h1>
            <p className="signin-left-subtitle">Sign in to access your account, track orders, and enjoy exclusive benefits.</p>

            <div className="signin-benefits">
              <div className="signin-benefit">
                <span className="signin-benefit-icon">✓</span>
                <span>Track your orders</span>
              </div>
              <div className="signin-benefit">
                <span className="signin-benefit-icon">✓</span>
                <span>Save items to your wishlist</span>
              </div>
              <div className="signin-benefit">
                <span className="signin-benefit-icon">✓</span>
                <span>Access exclusive deals</span>
              </div>
              <div className="signin-benefit">
                <span className="signin-benefit-icon">✓</span>
                <span>Faster checkout</span>
              </div>
            </div>
          </div>
        </div>

        <div className="signin-right">
          <div className="signin-form-container">
            <div className="signin-form-header">
              <h2 className="signin-form-title">{isSignUp ? 'Create Account' : 'Sign In'}</h2>
              <p className="signin-form-subtitle">
                {isSignUp ? `Join ${siteName} for exclusive benefits` : `Sign in to your ${siteName} account`}
              </p>
            </div>

            {error && (
              <div className="signin-error">
                <span>{error}</span>
              </div>
            )}

            <form className="signin-form" onSubmit={handleSubmit}>
              {isSignUp && (
                <div className="signin-input-group">
                  <label className="signin-label">Full Name</label>
                  <div className="signin-input-wrapper">
                    <User className="signin-input-icon" size={18} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                      className="signin-input"
                    />
                  </div>
                </div>
              )}

              <div className="signin-input-group">
                <label className="signin-label">Email Address</label>
                <div className="signin-input-wrapper">
                  <Mail className="signin-input-icon" size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email"
                    className="signin-input"
                  />
                </div>
              </div>

              <div className="signin-input-group">
                <label className="signin-label">Password</label>
                <div className="signin-input-wrapper">
                  <Lock className="signin-input-icon" size={18} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Enter your password"
                    className="signin-input"
                  />
                  <button
                    type="button"
                    className="signin-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              {isSignUp && (
                <div className="signin-input-group">
                  <label className="signin-label">Confirm Password</label>
                  <div className="signin-input-wrapper">
                    <Lock className="signin-input-icon" size={18} />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm your password"
                      className="signin-input"
                    />
                    <button
                      type="button"
                      className="signin-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              )}

              {!isSignUp && (
                <div className="signin-options">
                  <label className="signin-remember">
                    <input type="checkbox" />
                    <span>Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="signin-forgot">
                    Forgot Password?
                  </Link>
                </div>
              )}

              <button type="submit" className="signin-submit" disabled={isLoading}>
                {isLoading ? (
                  <span className="signin-loading">Please wait...</span>
                ) : (
                  <>
                    {isSignUp ? 'Create Account' : 'Sign In'}
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>

            <div className="signin-divider">
              <span>OR</span>
            </div>

            <div className="signin-social">
              <button className="signin-social-btn google">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>
              <button className="signin-social-btn facebook">
                <svg viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Continue with Facebook
              </button>
            </div>

            <div className="signin-footer">
              <p>
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
                <button type="button" className="signin-toggle" onClick={toggleMode}>
                  {isSignUp ? 'Sign In' : 'Create Account'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
